import { apiClient, type ApiRequestConfig } from './api';
type InviteRequest = Pick<ApiRequestConfig, 'signal' | 'assertRequestCurrent'> & { expectedUserId?: string };
const inviteRecoveryRequest = ({ expectedUserId, ...request }: InviteRequest): ApiRequestConfig => ({
  handleAccountSetup: true, ...request,
  ...(expectedUserId ? { headers: { 'X-TARK-Expected-User-Id': expectedUserId } } : {}),
});
import type {
  Squad,
  SquadInvitePreview,
  SquadAddMemberPayload,
  SquadChatMessage,
  SquadChatMessagesResponse,
  SquadMemberSummary,
  SendSquadChatMessagePayload,
  SquadUpdateMemberRolePayload,
} from '../types';

export async function getMySquads(): Promise<Squad[]> {
  const response = await apiClient.get<Squad[]>('/api/v1/web/squads/my');
  return response.data;
}

export async function previewSquadInvite(codeOrToken: string, request: InviteRequest = {}): Promise<SquadInvitePreview> {
  const response = await apiClient.get<SquadInvitePreview>(
    `/api/v1/web/squads/preview/${encodeURIComponent(codeOrToken)}`, inviteRecoveryRequest(request)
  );
  return response.data;
}

export async function joinSquadFromInvite(codeOrToken: string, request: InviteRequest = {}): Promise<Squad> {
  const response = await apiClient.post<Squad>('/api/v1/web/squads/join', {
    inviteCodeOrToken: codeOrToken,
  }, inviteRecoveryRequest(request));
  return response.data;
}

export async function getSquadDetail(squadId: number | string): Promise<Squad> {
  const response = await apiClient.get<Squad>(`/api/v1/web/squads/${squadId}`);
  return response.data;
}

export async function getSquadMembers(squadId: number | string): Promise<SquadMemberSummary[]> {
  const response = await apiClient.get<SquadMemberSummary[]>(`/api/v1/web/squads/${squadId}/members`);
  return response.data;
}

export async function addSquadMember(
  squadId: number | string,
  payload: SquadAddMemberPayload
): Promise<SquadMemberSummary> {
  const response = await apiClient.post<SquadMemberSummary>(`/api/v1/web/squads/${squadId}/members`, payload);
  return response.data;
}

export async function updateSquadMemberRole(
  squadId: number | string,
  payload: SquadUpdateMemberRolePayload
): Promise<SquadMemberSummary> {
  const response = await apiClient.put<SquadMemberSummary>(`/api/v1/web/squads/${squadId}/members/role`, payload);
  return response.data;
}

export async function removeSquadMember(squadId: number | string, userId: number): Promise<void> {
  await apiClient.delete(`/api/v1/web/squads/${squadId}/members/${userId}`);
}

export async function getSquadMessages(squadId: number | string, limit = 30): Promise<SquadChatMessagesResponse> {
  const response = await apiClient.get<SquadChatMessagesResponse>(`/api/v1/web/squads/${squadId}/messages`, {
    params: { limit },
  });
  return response.data;
}

export async function sendSquadMessage(
  squadId: number | string,
  payload: SendSquadChatMessagePayload
): Promise<SquadChatMessage> {
  const response = await apiClient.post<SquadChatMessage>(`/api/v1/web/squads/${squadId}/messages`, payload);
  return response.data;
}

export async function markSquadRead(squadId: number | string, lastReadMessageId: string): Promise<void> {
  await apiClient.post(`/api/v1/web/squads/${squadId}/read-visible`, { lastReadMessageId });
}
